/**
 * A contact field drawn as a chip — its group tag, then its name — the
 * concept's `.sb-field`. "PROFILE City ▾".
 *
 * ## WHY A FIELD LOOKS LIKE A CHIP AND NOT A FORM CONTROL
 *
 * In the Blueprint a field is a *thing* a rule is about, and the chip says so:
 * violet, because a field is part of Wend's model of a person, and tagged with
 * its group, because "City" from a profile and "City" from a Reach lead form
 * are different facts with different coverage. The rejected alternative —
 * a plain `Select` with the field name in it — reads as "pick any string" and
 * loses the group, which is the part a person needs to judge the rule.
 *
 * Contrast: `brand-ink-strong` on `brand-a/16` is 12.29:1 over surface-1 and
 * the open or hover fill `brand-a/24` keeps it above 10:1. The concept's
 * hover went to 0.26; the tint ladder for new code rounds it to /24.
 *
 * ## TWO ELEMENTS, ONE SKIN
 *
 * `as="span"` is a read-only display — a stored mapping on a detail page.
 * `as="button"` is the trigger for a picker the caller owns: it carries
 * `aria-haspopup="listbox"` and `aria-expanded`, and the focus ring. When the
 * picker is a Radix `Select`, use `<Select appearance="chip">` instead, which
 * draws this same skin on Radix's own trigger; `FIELD_CHIP_CLASS` is exported
 * for exactly that, so the two cannot drift.
 *
 * ## THE HOLE IT LEAVES
 *
 * The group tag's word is the raw group id ("orders", "consent"), upper-cased
 * by the tag's style. If a group ever needs a display name that differs from
 * its id, that belongs in `lib/identity.ts` beside its tone, not here.
 */
import { ChevronDown } from 'lucide-react';
import type { ButtonHTMLAttributes } from 'react';

import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';
import { FIELD_GROUP_TONE, type FieldGroup } from '../lib/identity';
import { Tag } from './Tag';

/** The chip skin, shared with `Select appearance="chip"`. */
export const FIELD_CHIP_CLASS = cn(
  'inline-flex max-w-full items-center gap-1.5 rounded-sm border border-brand-b/45 bg-brand-a/16',
  'py-[5px] pl-1.5 pr-2 text-[0.84rem] font-medium leading-snug text-brand-ink-strong',
  'transition-colors duration-150',
);

export interface FieldChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  group: FieldGroup;
  label: string;
  /** Default `'span'`. `'button'` makes it a picker trigger. */
  as?: 'span' | 'button';
  /** The picker this chip opens is open. */
  open?: boolean;
  /** Draw the ▾. Defaults to true for a button and false for a span. */
  chevron?: boolean;
}

export function FieldChip({ group, label, as = 'span', open = false, chevron, className, ...rest }: FieldChipProps) {
  const showChevron = chevron ?? as === 'button';
  const contents = (
    <>
      <Tag inset tone={FIELD_GROUP_TONE[group]}>{group}</Tag>
      <span className="min-w-0 truncate">{label}</span>
      {showChevron && <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 text-brand-ink" />}
    </>
  );

  if (as === 'button') {
    return (
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(FIELD_CHIP_CLASS, 'cursor-pointer hover:bg-brand-a/24', open && 'bg-brand-a/24', focusRing, className)}
        {...rest}
      >
        {contents}
      </button>
    );
  }

  return <span className={cn(FIELD_CHIP_CLASS, open && 'bg-brand-a/24', className)}>{contents}</span>;
}
